import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Skeleton } from '../ui';
import type { DashboardRFQ } from '../../hooks/useDashboard';
import type { RowResearch } from '../../hooks/useResearchProgress';
import { StageTag, TOTAL_STEPS, researchLine, timeAgo } from './RFQRowParts';

/**
 * The same rows on a phone. A five-column grid at 375px gives every column
 * about 60px, which is not enough for a project name, so the row becomes a
 * card: the name and its stage on the first line, the id and owner under it,
 * and the three scannable numbers — progress, parts, last touched — on one
 * quiet line at the bottom. Nothing is dropped and nothing is summarised;
 * only the arrangement changes.
 *
 * There is no sorting here on purpose. Sorting exists to find one row among
 * many by eye across aligned columns, and on a phone there are no columns to
 * align. The filters above do that job instead, and they stay.
 */
export const MobileRfqList: React.FC<{
    rows: DashboardRFQ[];
    research: Record<string, RowResearch>;
    empty: string;
    loading: boolean;
    onRowClick: (id: string) => void;
}> = ({ rows, research, empty, loading, onRowClick }) => {
    // "You have no active RFQs" while the list is still arriving is a claim
    // about the org that nobody has checked yet. The desktop table draws
    // skeleton rows for exactly this; the cards do the same.
    if (loading) {
        return (
            <div aria-hidden>
                {[0, 1, 2].map((i) => (
                    <div key={i} className="flex flex-col gap-s3 border-b border-line-divider px-s5 py-s5 last:border-b-0">
                        <Skeleton width="62%" height={11} />
                        <Skeleton width="38%" height={9} />
                    </div>
                ))}
            </div>
        );
    }
    if (rows.length === 0) {
        return <p className="m-0 px-s5 py-s7 text-center text-body-sm text-ink-muted">{empty}</p>;
    }
    return (
        <ul className="m-0 list-none p-0">
            {rows.map((row) => (
                <li key={row.id}>
                    <button
                        type="button"
                        onClick={() => onRowClick(row.id)}
                        className="flex w-full flex-col gap-s2 border-b border-line-divider px-s5 py-s4 text-left last:border-b-0 focus:outline-none focus-visible:bg-surface-hover active:bg-surface-hover"
                    >
                        <span className="flex w-full items-start gap-s3">
                            <span className="min-w-0 flex-1">
                                <span className={`block truncate text-[14px] ${row.name ? 'font-semibold text-ink' : 'font-medium text-ink-muted'}`}>
                                    {row.name ?? 'Untitled project'}
                                </span>
                                <span className="mt-[2px] block truncate font-mono text-[11px] text-ink-muted">
                                    {row.id}{row.owner ? ` · ${row.owner}` : ''}
                                </span>
                            </span>
                            <ChevronRight className="mt-[3px] h-4 w-4 shrink-0 text-ink-muted" />
                        </span>
                        <span className="flex w-full flex-wrap items-center gap-x-s3 gap-y-s2">
                            <StageTag row={row} research={research[row.id]} />
                            {row.pendingOnEndUser && (
                                <span className="whitespace-nowrap rounded-[6px] border border-warn-border bg-warn-bg px-[6px] py-[1px] text-[10.5px] font-semibold text-warn-ink">
                                    {row.iAmEndUser ? 'Yours to fill' : 'With end user'}
                                </span>
                            )}
                            <span className="ml-auto font-mono text-[11px] text-ink-muted">
                                {research[row.id]
                                    ? researchLine(research[row.id])
                                    : `${row.stageStep}/${TOTAL_STEPS} · ${row.partsCount} parts · ${timeAgo(row.updatedAtIso)}`}
                            </span>
                        </span>
                    </button>
                </li>
            ))}
        </ul>
    );
};
